/* ponder-sdk.js  —  Pure-JS voice-agent client (ES module) */

export default class Ponder {
    /**
     * Create a new Ponder client instance.
     * @param {Object}   cfg
     * @param {string}   cfg.assistantId
     * @param {string}   cfg.host                    http(s)://…
     * @param {Array}    [cfg.actions=[]]            [{ name, description, arguments:[{name,type,description,required}] , function }]
     * @param {string}   [cfg.instructions=""]
     */
    constructor({
        assistantId,
        host,
        actions = [],
        instructions = ""
    }) {
        this.assistantId = assistantId;
        this.rawHost = host;
        this.actions = actions;
        this.instructions = instructions;

        this.onSpeechStart = (() => { });
        this.onSpeechEnd = (() => { });
        this.onMessage = (() => { });
        this.onConnected = (() => { });
        this.onDisconnected = (() => { });
        this.onFunctionCall = ((name, args) => { });

        // internals
        this._audioCtx = null;
        this._mediaStream = null;
        this._mediaSource = null;
        this._workletNode = null;
        this._ws = null;

        this._messages = [];
        this._isUserSpeaking = false;
        this._activeAudioSources = [];
        this._nextTime = null;
        this._blockAudio = false;
        this._onConnectedInternal = (() => { });
        this._onDisconnectedInternal = (() => { });
        this._onMessageInternal = (() => { });
    }


    /* ───────────────────────── PUBLIC API ────────────────────────── */

    /**
     * Start the voice session.
     * If already connected, does nothing.
     * @returns {Promise<void>}
     */
    async connect() {
        if (this._ws || this._audioCtx) return;                       // already connected

        this._audioCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16_000 });
        await this.#_startMicStreaming();

        const wsUrl = this.#_toWsUrl(this.rawHost) + "/ws/talk";
        this._ws = new WebSocket(wsUrl);

        this._ws.onopen = () => this.#_initSession();
        this._ws.onclose = () => {this.disconnect().then(() => {
            this._onDisconnectedInternal();
            this.onDisconnected();
        })
        };

        this._ws.onerror = () => console.error("Error connecting Ponder");
        this._ws.onmessage = (evt) => this.#_handleWsMessage(JSON.parse(evt.data));
    }

    /**
     * Stop the voice session and release all resources.
     * @returns {Promise<void>}
     */
    async disconnect() {
        // stop mic & worklet
        if (this._workletNode) {
            this._workletNode.port.onmessage = null;
            this._workletNode.disconnect();
            this._workletNode = null;
        }
        if (this._mediaSource) { this._mediaSource.disconnect(); this._mediaSource = null; }
        if (this._mediaStream) { this._mediaStream.getTracks().forEach(t => t.stop()); this._mediaStream = null; }

        // audio nodes playing TTS
        this._activeAudioSources.forEach(src => src.stop());
        this._activeAudioSources = [];
        this._nextTime = null;

        if (this._audioCtx && this._audioCtx.state !== "closed") {
            try { await this._audioCtx.close(); } catch { }
        }
        this._audioCtx = null;

        // websocket
        if (this._ws && (this._ws.readyState === WebSocket.OPEN || this._ws.readyState === WebSocket.CONNECTING)) {
            try {
                this._ws.close();
            } catch { }
        }
        this._ws = null;
    }

    /**
     * Dynamically update the available actions (tools) after construction.
     * @param {Array} actions - Array of action definitions. format: [{ name, description, arguments: [{name, type, description, required}] }]
     */
    updateActions(actions) {
        this.actions = actions;
        if (this._ws && this._ws.readyState === WebSocket.OPEN) {
            this._ws.send(JSON.stringify({ type: "update_tools", tools: this.#_actionsToTools(actions) }));
        }
    }

    /**
     * Dynamically update the instructions for the assistant after construction.
     * @param {string} instr - New instructions.
     */
    updateInstructions(instr) {
        this.instructions = instr;
        if (this._ws && this._ws.readyState === WebSocket.OPEN) {
            this._ws.send(JSON.stringify({ type: "update_instructions", instructions: instr }));
        }
    }

    /**
     * Send a text message to the assistant (chat input).
     * @param {string} text - The text message to send.
     */
    sendTextMessage(text) {
        if (!text || !text.trim()) return;
        this.#_stopTtsPlayback();
        const userMessage = {
            role: "user",
            text: text.trim(),
            type: "text"
        };
        this._messages.push(userMessage);
        if (this._ws && this._ws.readyState === WebSocket.OPEN) {
            this._ws.send(JSON.stringify({
                type: "text_input",
                messages: this._messages
            }));
        }
    }

    /* ────────────────────── INTERNAL UTILITIES ───────────────────── */

    #_toWsUrl(url) {
        // Remove trailing slash if present
        let cleanUrl = url.endsWith('/') ? url.slice(0, -1) : url;
        if (cleanUrl.startsWith("https://")) return "wss://" + cleanUrl.slice(8);
        if (cleanUrl.startsWith("http://")) return "ws://" + cleanUrl.slice(7);
        return cleanUrl; // already ws:// or wss://
    }

    #_actionsToTools(actions) {
        return actions.map(a => ({
            type: "function",
            function: {
                name: a.name,
                description: a.description,
                parameters: {
                    type: "object",
                    required: (a.arguments ?? []).filter(arg => arg.required).map(arg => arg.name),
                    additionalProperties: false,
                    properties: Object.fromEntries(
                        (a.arguments ?? []).map(arg => [arg.name, { type: arg.type, description: arg.description }])
                    )
                }
            }
        }));
    }

    async #_startMicStreaming() {
        /* 1. getUserMedia */
        this._mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: { channelCount: 1, echoCancellation: true, autoGainControl: true, noiseSuppression: true }
        });
        this._mediaSource = this._audioCtx.createMediaStreamSource(this._mediaStream);

        /* 2. create AudioWorklet that forwards Float32 chunks to JS main thread */
        const workletURL = URL.createObjectURL(new Blob([`
        class PonderMicProcessor extends AudioWorkletProcessor {
          process(inputs) {
            if (inputs[0] && inputs[0][0]) this.port.postMessage(inputs[0][0]);
            return true;
          }
        }
        registerProcessor('ponder-mic-processor', PonderMicProcessor);
      `], { type: "application/javascript" }));
        await this._audioCtx.audioWorklet.addModule(workletURL);
        URL.revokeObjectURL(workletURL);

        this._workletNode = new AudioWorkletNode(this._audioCtx, 'ponder-mic-processor');
        this._workletNode.port.onmessage = ({ data }) => {
            if (this._ws?.readyState === WebSocket.OPEN && !this._blockAudio) {
                this._ws.send(JSON.stringify({
                    type: "audio_input",
                    audio: Array.from(data),
                    messages: this._messages,
                }));
            }
        };

        /* avoid feedback */
        const silentGain = this._audioCtx.createGain();
        silentGain.gain.value = 0;

        this._mediaSource.connect(this._workletNode);
        this._workletNode.connect(silentGain);
        silentGain.connect(this._audioCtx.destination);
    }

    #_initSession() {
        this._ws.send(JSON.stringify({
            type: "init_session",
            assistant_id: this.assistantId,
            tools: this.#_actionsToTools(this.actions),
            instructions: this.instructions
        }));
    }

    #_handleWsMessage(evt) {
        switch (evt.type) {
            case "session_initialized":
                this._onConnectedInternal();
                this.onConnected();
                break;

            case "text":
                this._messages.push(evt);
                this._onMessageInternal(evt);
                this.onMessage(evt);
                break;

            case "speech_start":
                this._isUserSpeaking = true;
                this.#_stopTtsPlayback();
                this.onSpeechStart();
                break;

            case "speech_end":
                this._isUserSpeaking = false;
                this.onSpeechEnd();
                break;

            case "audio":
                if (!this._isUserSpeaking && this._audioCtx) {
                    const pcm = Uint8Array.from(atob(evt.audio), c => c.charCodeAt(0));
                    this.#_playPcmChunk(pcm);
                }
                break;

            case "function_call":
                this._messages.push(evt);
                this.#_invokeFunction(evt.function_calls[0]);
                break;
        }
    }

    #_playPcmChunk(u8) {
        if (u8.length % 2) return;
        const float32 = new Float32Array(new Int16Array(u8.buffer)).map(v => v / 32768);
        const buf = this._audioCtx.createBuffer(1, float32.length, this._audioCtx.sampleRate);
        buf.copyToChannel(float32, 0);

        const src = this._audioCtx.createBufferSource();
        src.buffer = buf;
        src.connect(this._audioCtx.destination);

        const startAt = (this._nextTime === null || this._nextTime < this._audioCtx.currentTime)
            ? this._audioCtx.currentTime
            : this._nextTime;

        src.start(startAt);
        this._nextTime = startAt + buf.duration;
        this._activeAudioSources.push(src);
    }

    #_stopTtsPlayback() {
        this._activeAudioSources.forEach(s => s.stop());
        this._activeAudioSources = [];
        this._nextTime = null;
    }

    async #_invokeFunction(call) {
        let response = "function not found";
        this._blockAudio = true;
        try{
            response = await this.onFunctionCall(call.name, JSON.parse(call.arguments));
            if (response === undefined) {
                response = "success";
            }
            console.log("Function response:", response);
        }
        catch (e) {
            response = "error calling function";
            console.error(e);
        }

        this._messages.push({ role: "tool", type: "function_response", response, call_id: call.call_id });
        if (this._ws?.readyState === WebSocket.OPEN) {
            this._ws.send(JSON.stringify({ type: "function_response", messages: this._messages }));
            this._blockAudio = false;
        }
    }
}
